import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Clock, Smartphone, Target } from "lucide-react";
import { toast } from "sonner";
import PublicLayout from "@/components/site/PublicLayout";
import { IconAvatar } from "@/components/app/IconAvatar";
import { acceptInvite, isAuthenticated, previewInvite } from "@/lib/api";
import { rememberReturn } from "@/lib/returnTo";
import { getUser } from "@/lib/storage";

const STATE_COPY = {
  revoked: "This invite was revoked.",
  expired: "This invite has expired.",
  used_up: "This invite has already been used.",
};

export const APP_INVITE_URL = (code) => `hustlrmate://invite/${code}`;
const isAndroid = () => /android/i.test(navigator.userAgent || "");

function untilLabel(value) {
  if (!value) return "Never expires";
  const ms = new Date(value).getTime() - Date.now();
  if (ms <= 0) return "Expired";
  const hours = Math.round(ms / 3600000);
  return hours < 48 ? `Expires in ${Math.max(1, hours)} hour${hours === 1 ? "" : "s"}` : `Expires in ${Math.round(hours / 24)} days`;
}

export default function InvitePage() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const signedIn = isAuthenticated();
  const me = getUser();
  const path = `/invite/${code}`;

  useEffect(() => {
    let active = true;
    previewInvite(code)
      .then((result) => { if (active) setInvite(result); })
      .catch((caught) => { if (active) setError(caught.message || "This invite could not be opened."); });
    return () => { active = false; };
  }, [code]);

  const accept = async () => {
    setBusy(true);
    try {
      const result = await acceptInvite(code);
      toast(`You and ${result.partner.fullName.split(" ")[0]} are partners.`, { description: result.cohort ? `You're doing ${result.cohort.name} together, and your Starter Sprint has begun.` : "Your seven-day Starter Sprint has begun." });
      navigate(`/workspace/${result.partnershipId}`);
    } catch (caught) {
      if (caught.code === "profile_incomplete") {
        rememberReturn(path);
        toast("Finish your profile first.", { description: "You'll come straight back to this invite." });
        navigate("/onboarding");
      } else if (caught.code === "already_partners" && caught.data?.detail?.partnershipId) {
        navigate(`/workspace/${caught.data.detail.partnershipId}`);
      } else {
        toast("Could not accept this invite.", { description: caught.message });
      }
    } finally {
      setBusy(false);
    }
  };

  const goToAuth = (mode) => {
    rememberReturn(path);
    navigate(`/auth?mode=${mode}`);
  };

  const inviter = invite?.inviter;
  const firstName = inviter?.fullName?.split(" ")[0] || "Someone";
  const isOwn = Boolean(signedIn && me?.id && inviter?.userId === me.id);
  const live = invite?.state === "live";

  return (
    <PublicLayout>
      <div className="mx-auto max-w-xl px-6 sm:px-10">
        <div className="overline mb-4">Partner invite</div>
        {!invite && !error && <div className="text-sm text-[#A1A1AA]">Opening invite…</div>}
        {error && (
          <section className="hm-card rounded-md p-8">
            <h1 className="font-serif-editorial text-4xl font-light tracking-tight">Invite not found</h1>
            <p className="mt-3 text-sm text-[#A1A1AA]">{error}</p>
            <Link to="/cohorts" className="hm-btn-primary mt-8 inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm font-semibold">
              Join a cohort instead <ArrowRight className="h-4 w-4" />
            </Link>
          </section>
        )}
        {invite && (
          <section className="hm-card rounded-md p-8 sm:p-10" data-testid="invite-card">
            <div className="flex items-center gap-4">
              <IconAvatar iconKey={inviter.profileIcon} size="lg" />
              <div className="min-w-0">
                <div className="truncate text-lg text-[#F5F5F0]">{inviter.fullName}</div>
                {inviter.username && <div className="text-xs text-[#A1A1AA]">@{inviter.username}</div>}
              </div>
            </div>
            <h1 className="mt-8 font-serif-editorial text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl text-balance">
              {isOwn ? "This is your invite." : invite.cohort
                ? <>{firstName} wants to do <em className="italic text-[#F59E0B] lining-nums">{invite.cohort.name}</em> with you.</>
                : <>{firstName} wants you as their <em className="italic text-[#F59E0B]">accountability partner.</em></>}
            </h1>
            {invite.note && <p className="mt-5 border-l-2 border-[#F59E0B]/60 pl-4 text-sm italic text-[#D4D4D8]">“{invite.note}”</p>}
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-[#A1A1AA]">
              {inviter.goal && <span className="inline-flex items-center gap-1.5"><Target className="h-3.5 w-3.5" /> {inviter.goal}{inviter.target ? ` · ${inviter.target}` : ""}</span>}
              {live && <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {untilLabel(invite.expiresAt)}</span>}
            </div>

            {!live ? (
              <div className="mt-8">
                <p className="text-sm text-[#F5F5F0]">{STATE_COPY[invite.state] || "This invite is no longer available."}</p>
                <p className="mt-2 text-sm text-[#A1A1AA]">Ask {firstName} for a new one, or join the next cohort and we'll pair you.</p>
                <Link to="/cohorts" className="hm-btn-ghost mt-6 inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm">See cohorts <ArrowRight className="h-4 w-4" /></Link>
              </div>
            ) : isOwn ? (
              <div className="mt-8">
                <p className="text-sm text-[#A1A1AA]">Share this page or the code <span className="font-mono tracking-[0.2em] text-[#F5F5F0]">{invite.code}</span> with the person you want to partner with.</p>
                <Link to="/matches" className="hm-btn-ghost mt-6 inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm">Manage invites <ArrowRight className="h-4 w-4" /></Link>
              </div>
            ) : (
              <div className="mt-8">
                <p className="text-sm leading-relaxed text-[#A1A1AA]">
                  {invite.cohort
                    ? `Accept and you two join the cohort as partners, starting ${new Date(`${invite.cohort.startsOn}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" })}. A shared workspace opens with a seven-day Starter Sprint — daily check-ins, shared tasks and a focus room.`
                    : "Accept and you two skip matching: a shared workspace opens with a seven-day Starter Sprint — daily check-ins, shared tasks and a focus room where each of you can see whether the other showed up."}
                </p>
                {signedIn ? (
                  <button onClick={accept} disabled={busy} data-testid="invite-accept" className="hm-btn-primary mt-7 inline-flex items-center gap-2 rounded-md px-6 py-3.5 text-sm font-semibold disabled:opacity-60">
                    {busy ? "Starting…" : `Partner with ${firstName}`} <ArrowRight className="h-4 w-4" />
                  </button>
                ) : (
                  <div className="mt-7 flex flex-wrap items-center gap-3">
                    <button onClick={() => goToAuth("signup")} data-testid="invite-signup" className="hm-btn-primary inline-flex items-center gap-2 rounded-md px-6 py-3.5 text-sm font-semibold">
                      Create a free account to accept <ArrowRight className="h-4 w-4" />
                    </button>
                    <button onClick={() => goToAuth("signin")} className="hm-btn-ghost rounded-md px-5 py-3.5 text-sm">I have an account</button>
                  </div>
                )}
                {isAndroid() && (
                  <a href={APP_INVITE_URL(invite.code)} className="mt-5 inline-flex items-center gap-2 text-xs text-[#A1A1AA] underline-offset-4 hover:text-[#F5F5F0] hover:underline">
                    <Smartphone className="h-3.5 w-3.5" /> Open in the HustlrMate app
                  </a>
                )}
                <p className="mt-6 text-xs text-[#7c7c84]">Invite code <span className="font-mono tracking-[0.2em] text-[#A1A1AA]">{invite.code}</span> · you can also enter it in the app under Partners.</p>
              </div>
            )}
          </section>
        )}
      </div>
    </PublicLayout>
  );
}
